/**
 * The safety net behind a one click delete.
 *
 * It exists so deleting from the week view does not need a confirm dialog in
 * front of every deliberate delete. That trade only works if the undo is easy
 * to notice and hard to miss the window on, so:
 *
 *  - it runs for 12 seconds, not the 4 or 5 a notification toast usually gets,
 *    because the mistake is often noticed a beat after the click
 *  - the remaining time is drawn as a draining bar, so nobody has to guess
 *    whether it is about to vanish
 *  - hovering or focusing it stops the clock entirely. Somebody moving the
 *    pointer towards Undo must never lose it on the way, which is the same rule
 *    the hover panels follow
 *  - it never covers the roster it refers to: it sits above the bottom edge and
 *    out of the way of the totals bar
 */
import { useEffect, useRef, useState } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { Spinner } from './Bits.tsx';

const WINDOW_MS = 12_000;

export function UndoBar({
  open,
  label,
  busy,
  onUndo,
  onDismiss,
}: {
  open: boolean;
  label: string;
  busy: boolean;
  onUndo: () => void;
  onDismiss: () => void;
}) {
  const [remaining, setRemaining] = useState(WINDOW_MS);
  const [paused, setPaused] = useState(false);
  const startedAt = useRef(0);
  const leftWhenPaused = useRef(WINDOW_MS);

  useEffect(() => {
    if (!open) return;
    setRemaining(WINDOW_MS);
    leftWhenPaused.current = WINDOW_MS;
    startedAt.current = Date.now();
  }, [open, label]);

  useEffect(() => {
    if (!open || paused || busy) return;
    startedAt.current = Date.now();
    const tick = setInterval(() => {
      const left = leftWhenPaused.current - (Date.now() - startedAt.current);
      if (left <= 0) {
        clearInterval(tick);
        onDismiss();
      } else {
        setRemaining(left);
      }
    }, 100);
    return () => clearInterval(tick);
  }, [open, paused, busy, onDismiss]);

  if (!open) return null;
  const pct = Math.max(0, Math.min(100, (remaining / WINDOW_MS) * 100));

  const hold = () => {
    leftWhenPaused.current = remaining;
    setPaused(true);
  };
  const release = () => {
    startedAt.current = Date.now();
    setPaused(false);
  };

  return (
    <div
      className="anim-undo-in fixed left-1/2 z-[8000] w-[min(420px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden"
      style={{
        bottom: 'calc(1rem + env(safe-area-inset-bottom))',
        borderRadius: 'var(--sp-radius-lg)',
        background: 'var(--sp-surface)',
        border: '1px solid var(--sp-border)',
        boxShadow: 'var(--sp-shadow-4)',
      }}
      role="status"
      onMouseEnter={hold}
      onMouseLeave={release}
      onFocusCapture={hold}
      onBlurCapture={release}
    >
      <div className="flex items-center gap-2.5 px-3.5 py-2.5">
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">{label}</span>
        <button type="button" className="btn btn-sm btn-primary" onClick={onUndo} disabled={busy}>
          {busy ? (
            <Spinner size={14} label="Putting it back" />
          ) : (
            <>
              <RotateCcw size={14} aria-hidden="true" />
              Undo
            </>
          )}
        </button>
        <button
          type="button"
          className="btn btn-icon btn-sm btn-ghost"
          onClick={onDismiss}
          title="Dismiss"
          aria-label="Dismiss"
        >
          <X size={15} aria-hidden="true" />
        </button>
      </div>
      {/* How long is left, drawn rather than counted, and visibly frozen while
          the pointer is over the bar. */}
      <div style={{ height: 3, background: 'var(--sp-surface-2)' }}>
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: paused ? 'var(--sp-text-faint)' : 'var(--sp-accent)',
            transition: 'width 100ms linear',
          }}
        />
      </div>
    </div>
  );
}
